import { useState, useEffect, useCallback } from 'react';

/**
 * Shared state management for paginated inventory master list pages.
 *
 * Handles: items, loading, error, server-side pagination, debounced search,
 * and status filtering. Pages own their own load() function and any
 * entity-specific extra filters (e.g. typeFilter), keeping the hook thin
 * and composable.
 *
 * Usage:
 *   const list = useMasterList({ pageSize: UOM_PAGE_SIZE });
 *   const { items, setItems, totalCount, setTotalCount, ... } = list;
 *
 *   const load = useCallback(async () => {
 *     list.setLoading(true);
 *     try {
 *       const raw = await getUoms(buildParams(list));
 *       const { items, total } = toInventoryPage(raw, list);
 *       list.setItems(items.map(normalizeUom));
 *       list.setTotalCount(total);
 *     } catch (e) {
 *       list.setError(e.message);
 *     } finally {
 *       list.setLoading(false);
 *     }
 *   }, [list.page, list.search, list.statusFilter]);
 */
export default function useMasterList({ pageSize = 10 } = {}) {
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const resetFilters = useCallback(() => {
    setSearchInput('');
    setSearch('');
    setStatusFilter('');
    setPage(0);
  }, []);

  return {
    items, setItems,
    totalCount, setTotalCount,
    loading, setLoading,
    error, setError,
    page, setPage,
    searchInput, setSearchInput,
    search,
    statusFilter, setStatusFilter,
    resetFilters,
    pageSize,
  };
}
